// src/pos-app/surfaces/waiter/TablesSurface.js
//
// F12a-N Task W1 (skeleton) / W2 (floor + popover) — native Waiter Tables
// surface (design contract: docs/mockups/f12a-n/waiter-tables.html).
// Mounted by WaiterStation's Tables tab when SURFACES.waiter === 'native'
// (surfaces/config.js). WaiterStation keeps the business-day gate ABOVE
// this surface (DayClosedScreen while closed), so this component can
// assume an open business day.
//
// Layout frame (HARD no-scroll rule, spec §6): a flex row that fills the
// station's content slot exactly —
//   [floor column flex:1]                     — dining tabs (flex:none) over
//                                               the floor-canvas zone (flex:1)
//   [right panel 40%]                         — Waitlist | Reservations shell
// Nothing page-scrolls; only the panel body (and the floor canvas, which
// scales-to-fit instead of scrolling) may render content that would
// otherwise overflow. Portrait/narrow reflow stacks the panel under the
// floor at 40% height, same rule.
//
// W2 wires the real floor: <FloorCanvas/> (tiles, legend, mode banner,
// merge bar, tap→popover) replaces W1's placeholder box, plus the
// occupations/pendingOrders data + 30s refreshAll poll and the merge /
// seat(change-table) mode STATE (TablesSurface owns state, FloorCanvas
// renders + reports taps — see that file's header). Business logic for the
// multi-step flows (merge confirm + order-folding, seat-mode resolution)
// is a faithful extraction in ./lib/tableFlow.js; trivial one-call actions
// (seat walk-in, mark served, request bill, clear table, split group) stay
// inline here, same proportionality as the legacy page.
//
// W4 replaces the New-Order/Add-Items stub with the real drawer:
// TablesSurface owns `orderDrawer` state (mode/tableNumber/occupation/
// existingOrder — the identical shape TableServicePage.js:429-443 builds)
// and mounts exactly one <OrderDrawer/>, display-toggled by `isOpen` like
// every other overlay in this file — never conditionally unmounted, so an
// in-progress cart survives incidental re-renders. handleAddItems seeds
// existingOrder from `pendingOrders[tableKey]` exactly like the legacy
// handler (435-443); the drawer's own submit engine
// (./lib/drawerFlow.submitOrder, a faithful extraction of
// WaiterOrderDrawer.js's handleSubmit) does everything else — this surface
// only supplies onClose (clears orderDrawer) and onOrderPlaced (refreshAll
// + a mode-appropriate success toast, then the drawer's own onClose fires).
//
// W5 adds two things:
//
// 1. Escape-chain centralization — a SINGLE `window.addEventListener(
//    'keydown', ...)` in this component is now the one authority for
//    Escape across every overlay this surface can show, ranked topmost-
//    first (one layer dismissed per press, mirroring
//    surfaces/cashier/SellSurface.js's "Escape priority chain" — see that
//    file's comment for the precedent this follows): order drawer → table
//    popover → side-panel (waitlist/reservation) modals → the Sales PIN
//    modal → merge/seat-mode cancel. `window.__aqanaPrintDialogOpen` is
//    checked first too, purely for parity with that precedent — this
//    surface has no native print dialog today (no charge/receipt flow;
//    see the plan's "Charge / split-bill mechanics" note), so the guard is
//    currently a no-op, kept only so the chain's shape matches exactly and
//    costs nothing to leave in place.
//    The popover (FloorCanvas) and side-panel modals (SidePanel) each keep
//    their OWN local state (position math / form fields respectively) —
//    lifting that up here would be a much bigger refactor for no behavior
//    change — so both are wrapped in forwardRef (`closePopover`/
//    `closeModals` exposed via useImperativeHandle) and report their
//    open/closed state up through an optional callback prop
//    (`onPopoverOpenChange` / `onModalOpenChange`), read into
//    `popoverOpen`/`sidePanelModalOpen` state here. The order drawer's
//    `orderDrawer` state already lives here (W4), so it needs no such
//    plumbing — except for its nested REUSED AddonSelectionModal
//    (Modal.js-based, zero edits), which keeps its own Escape self-
//    dismissal (same reason SellSurface's chain leaves Modal.js-backed
//    reused overlays alone) and is gated via `orderDrawerAddonOpen`
//    (OrderDrawer's `onAddonModalOpenChange`) so a single Escape press
//    can't close both layers at once.
// 2. The Sales PIN modal's OPEN STATE is owned by WaiterStation (it must
//    survive this component unmounting when sales-mode itself flips on —
//    see WaiterStation.js's header), not here — `salesPinOpen`/
//    `onCloseSalesPin` arrive as plain props (same pattern as `locked`)
//    purely so this chain can rank and dismiss it; the modal's markup is
//    rendered by WaiterStation, not this component.
//    Review fix — `salesPinOpen` ALSO closes any in-progress order drawer
//    (effect next to the `locked`-closes-drawer one below): opening Sales
//    should not leave a mid-entry order sitting underneath the PIN prompt.
//    See SalesPinModal.js's header for the paired portal/z-index fix.
//
// W3 fills the right panel: waitlist + reservations state/loaders join the
// SAME refreshAll (extending the existing 30s poll — legacy 265-267 always
// refreshed all four sources together, one interval), the Upcoming-vs-Past
// split is derived here (legacy 306-313), and <SidePanel/> renders the
// tabs/entries/add-modals. Seat-from-waitlist / seat-reservation REUSE
// W2's seat-mode machinery: the panel's Seat buttons only call
// startSeatFromWaitlist / startSeatReservation (legacy 672-688), which set
// `seatMode` — FloorCanvas then shows the "tap a table" banner and routes
// the next tile tap through the existing handleSeatTarget →
// lib/tableFlow.seatOnTable ('waitlist' / 'reservation' branches, already
// extracted in W2).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage, getEntityTranslation } from '../../../context/LanguageContext';
import { diningOptionService, occupationService, waitingListService, reservationService, guestService } from '../../../services/api';
import api from '../../../services/api';
import tenantStorage from '../../../services/tenantStorage';
import dedupeByName from '../../../utils/dedupeByName';
import FloorCanvas from './FloorCanvas';
import SidePanel from './SidePanel';
import OrderDrawer from './OrderDrawer';
import { seatOnTable, confirmMerge } from './lib/tableFlow';

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// frame RTL-correct; all tap targets are ≥44px.
const CSS = `
.wtb-root{flex:1;height:100%;min-height:0;display:flex;position:relative;overflow:hidden;box-sizing:border-box;}

/* ---- floor column (mockup .floorcol) ---- */
.wtb-floorcol{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden;
  padding:12px 14px 10px;}

/* dining-option tabs + auto-refresh chip + merge toggle (mockup .toprow) */
.wtb-toprow{flex:none;display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;}
.wtb-pill{display:inline-flex;align-items:center;gap:7px;min-height:44px;padding:0 16px;
  border-radius:999px;background:var(--c-surface);border:1px solid var(--c-border);cursor:pointer;
  font-family:inherit;font-size:13.5px;font-weight:700;color:var(--c-slate);
  box-shadow:0 1px 2px rgba(2,8,23,.05);transition:border-color .12s, box-shadow .12s;}
.wtb-pill:hover{border-color:var(--c-greenBorder);}
.wtb-pill.active{background:var(--c-green);border-color:var(--c-green);color:#fff;
  box-shadow:0 4px 14px color-mix(in srgb, var(--c-green) 35%, transparent);}
.wtb-refresh{margin-inline-start:auto;display:inline-flex;align-items:center;gap:6px;
  font-size:11px;font-weight:700;color:var(--c-muted);
  background:var(--c-faint);border:1px solid var(--c-border);border-radius:999px;padding:5px 11px;}
.wtb-refresh svg{color:var(--c-success);}
.wtb-merge-toggle{display:inline-flex;align-items:center;gap:7px;min-height:44px;padding:0 15px;
  border-radius:999px;background:var(--c-surface);border:1.5px solid var(--c-border);cursor:pointer;
  font-family:inherit;font-size:12.5px;font-weight:700;color:var(--c-slate);}
.wtb-merge-toggle:hover{border-color:var(--c-greenBorder);color:var(--c-green);}
.wtb-merge-toggle.on{background:var(--c-greenLight);border-color:var(--c-green);color:var(--c-green);}

/* empty state — no dining option carries floor coordinates */
.wtb-nofloor{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:10px;text-align:center;padding:24px;color:var(--c-muted);}
.wtb-nofloor-ic{width:56px;height:56px;border-radius:18px;display:flex;align-items:center;justify-content:center;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-muted);}
.wtb-nofloor-t{font-size:14px;font-weight:700;color:var(--c-slate);max-width:320px;line-height:1.5;}

/* transient error/success toast (legacy TableServicePage.js:716-747) */
.wtb-toast{position:absolute;top:10px;left:50%;transform:translateX(-50%);z-index:50;
  max-width:420px;width:90%;padding:10px 16px;border-radius:10px;font-size:13px;font-weight:600;
  text-align:center;box-sizing:border-box;}
.wtb-toast.error{background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-red);}
.wtb-toast.success{background:var(--c-greenLight);border:1px solid var(--c-greenBorder);color:var(--c-greenDark);}

/* ---- right panel: Waitlist | Reservations (mockup .panel) ----
   Shell only — tabs/body/foot live in SidePanel (its own scoped CSS). */
.wtb-panel{flex:none;width:40%;min-width:0;min-height:0;display:flex;flex-direction:column;
  overflow:hidden;
  background:color-mix(in srgb, var(--c-surface) 78%, transparent);
  border-inline-start:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);
  -webkit-backdrop-filter:blur(20px) saturate(1.5);backdrop-filter:blur(20px) saturate(1.5);}

/* ---- portrait / narrow reflow — same hard no-scroll rule (mockup @media) ---- */
@media (max-width:1100px), (orientation:portrait){
  .wtb-root{flex-direction:column;}
  .wtb-panel{width:100%;height:40%;
    border-inline-start:none;
    border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
}
`;

const RefreshIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12a9 9 0 1 1-2.6-6.4" />
        <path d="M21 3v5h-5" />
    </svg>
);

const MergeIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="7" width="9" height="10" rx="2" /><rect x="13" y="7" width="9" height="10" rx="2" /><path d="M11 12h2" />
    </svg>
);

const TableIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11h18M12 11v9M5 11l1.5 9M19 11l-1.5 9M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

// W4 regression fix (task-n8 Bug-B class) — `locked` is WaiterStation's
// useOperatorSession().locked, threaded straight through as a prop (the
// cleanest existing signal: WaiterStation already owns it and re-renders
// this surface on every lock/unlock, same as every other prop here). An
// order left mid-entry when the station locks must not survive the
// handover — belt-and-braces on top of the OrderDrawer/OperatorLockScreen
// z-index fix (both portal to document.body; the drawer now sits below
// the lock screen there), this additionally unmounts the drawer outright
// so a new operator never inherits a stranger's in-progress cart.
export default function TablesSurface({
    locked = false,
    salesPinOpen = false,
    onCloseSalesPin = () => {},
}) {
    const { t, language, translationDetails } = useLanguage();

    const [diningOptions, setDiningOptions] = useState([]);
    const [diningOptionObjects, setDiningOptionObjects] = useState({});
    const [selectedDiningOption, setSelectedDiningOption] = useState('');
    const [optionsLoaded, setOptionsLoaded] = useState(false);

    const [occupations, setOccupations] = useState([]);
    const [pendingOrders, setPendingOrders] = useState({});
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [todayReservations, setTodayReservations] = useState([]);

    const [mergeMode, setMergeMode] = useState(false);
    const [mergeSelection, setMergeSelection] = useState([]); // table numbers; first = anchor
    const [seatMode, setSeatMode] = useState(null);
    const [orderDrawer, setOrderDrawer] = useState(null); // {mode, tableNumber, occupation, existingOrder}
    const [orderDrawerAddonOpen, setOrderDrawerAddonOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [sidePanelModalOpen, setSidePanelModalOpen] = useState(false);
    const floorCanvasRef = useRef(null);
    const sidePanelRef = useRef(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Faithful extraction — TableServicePage.js:197-221 (loadDiningOptions):
    // dedupeByName over diningOptionService.getAll(), then keep only options
    // whose tables_config carries real x/y coordinates (array form, plain
    // object form, and object-of-arrays form all handled), auto-selecting the
    // first qualifying option. W2 completes W1's partial extraction by also
    // building `diningOptionObjects` (legacy's `objMap`, TableServicePage.js
    // :210-214) — needed from here on for dining_option_id lookups (occupation
    // filtering, seat/merge payloads). Deviation kept from W1: the source
    // guards the auto-select with a stale `!selectedDiningOption` closure over
    // [] deps — here the identical behavior uses a functional update instead.
    const loadDiningOptions = useCallback(async () => {
        try {
            const all = dedupeByName(await diningOptionService.getAll());
            const withTables = all.filter(d => {
                if (!d.tables_config || Object.keys(d.tables_config).length === 0) return false;
                const cfg = d.tables_config;
                if (Array.isArray(cfg)) return cfg.some(tbl => tbl.x !== undefined && tbl.y !== undefined);
                if (typeof cfg === 'object') return Object.values(cfg).some(v => {
                    const entry = Array.isArray(v) && v.length > 0 ? v[0] : v;
                    return entry && entry.x !== undefined && entry.y !== undefined;
                });
                return false;
            });
            const objMap = {};
            withTables.forEach(d => { objMap[d.name] = d; });
            setDiningOptions(withTables);
            setDiningOptionObjects(objMap);
            if (withTables.length > 0) {
                setSelectedDiningOption(prev => prev || withTables[0].name);
            }
        } catch (e) {
            console.error('Failed to load dining options:', e);
        }
        setOptionsLoaded(true);
    }, []);

    // Faithful extraction — TableServicePage.js:236-243 (loadOccupations).
    const loadOccupations = useCallback(async () => {
        try {
            const occs = await occupationService.listActive();
            setOccupations(Array.isArray(occs) ? occs : []);
        } catch (e) {
            console.error('Failed to load occupations:', e);
        }
    }, []);

    // Faithful extraction — TableServicePage.js:223-234 (loadPendingOrders):
    // scoped to the active business day (salesDate), not the calendar date —
    // a business day can span midnight, and the merge-combine relies on every
    // active table draft being present here.
    const loadPendingOrders = useCallback(async () => {
        try {
            const salesDate = await tenantStorage.getState('salesDate');
            const params = salesDate ? { sales_date: salesDate } : {};
            const response = await api.get('/tenant-storage/orders', { params });
            const { orders } = response.data || {};
            setPendingOrders(orders || {});
        } catch (e) {
            console.error('loadPendingOrders failed:', e);
            setPendingOrders({});
        }
    }, []);

    // Faithful extraction — TableServicePage.js:245-252 (loadWaitlist).
    const loadWaitlist = useCallback(async () => {
        try {
            const entries = await waitingListService.list({ status: 'waiting,notified' });
            setWaitlistEntries(Array.isArray(entries) ? entries : []);
        } catch (e) {
            console.error('Failed to load waitlist:', e);
        }
    }, []);

    // Faithful extraction — TableServicePage.js:254-263 (loadReservations):
    // today's date built from LOCAL date parts, never toISOString.
    const loadReservations = useCallback(async () => {
        try {
            const today = new Date();
            const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const res = await reservationService.list({ date: dateStr });
            setTodayReservations(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error('Failed to load reservations:', e);
        }
    }, []);

    // Faithful extraction — TableServicePage.js:265-267, 282-285 (refreshAll +
    // the 30s poll). W3 completes W2's partial version: all four sources
    // refresh together on the ONE existing interval, exactly like legacy.
    const refreshAll = useCallback(async () => {
        await Promise.all([loadOccupations(), loadWaitlist(), loadReservations(), loadPendingOrders()]);
    }, [loadOccupations, loadWaitlist, loadReservations, loadPendingOrders]);

    useEffect(() => {
        loadDiningOptions();
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const id = setInterval(refreshAll, 30000);
        return () => clearInterval(id);
    }, [refreshAll]);

    useEffect(() => {
        if (!error) return undefined;
        const id = setTimeout(() => setError(''), 2500);
        return () => clearTimeout(id);
    }, [error]);

    useEffect(() => {
        if (!success) return undefined;
        const id = setTimeout(() => setSuccess(''), 2500);
        return () => clearTimeout(id);
    }, [success]);

    // W4 fix — close the order drawer on station lock (see the `locked`
    // prop's header note). Runs only on the false->true transition, so it
    // never fires on initial mount (locked starts false in every real
    // caller) and never fights an operator who is mid-order at unlock.
    useEffect(() => {
        if (locked) setOrderDrawer(null);
    }, [locked]);

    // W5 REVIEW FIX (finding 1, belt-and-braces half) — close any in-progress
    // order drawer the moment the Sales PIN modal opens. The modal itself was
    // fixed to portal to document.body (SalesPinModal.js's header note) so it
    // now correctly stacks ABOVE this surface's own portaled OrderDrawer
    // (z:60 vs 55/56) — but leaving a mid-entry order sitting underneath a
    // cashier-PIN prompt is confusing, and if the PIN succeeds this whole
    // surface unmounts anyway (WaiterStation swaps to SellSurface), silently
    // discarding that in-progress cart. Same one-line precedent as the
    // `locked` effect above (a mode switch, like a lock, should not leave an
    // order drawer stranded open). Runs only on the false->true transition.
    useEffect(() => {
        if (salesPinOpen) setOrderDrawer(null);
    }, [salesPinOpen]);

    /* ── Escape-chain centralization (Task W5) — single keydown authority
       for every overlay this surface can show. Topmost-first, one layer
       dismissed per press. See the header note above for the full
       rationale and mirrors surfaces/cashier/SellSurface.js's "Escape
       priority chain". ─────────────────────────────────────────────── */
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            // Parity with SellSurface's chain — no-op today (this surface
            // has no native print dialog; see header note).
            if (window.__aqanaPrintDialogOpen) return;

            if (orderDrawer) {
                // The REUSED AddonSelectionModal self-dismisses on Escape
                // (Modal.js); don't also close the drawer underneath it.
                if (orderDrawerAddonOpen) return;
                setOrderDrawer(null);
                return;
            }
            if (popoverOpen) {
                floorCanvasRef.current?.closePopover();
                return;
            }
            if (sidePanelModalOpen) {
                sidePanelRef.current?.closeModals();
                return;
            }
            if (salesPinOpen) {
                onCloseSalesPin();
                return;
            }
            if (mergeMode) {
                cancelMergeMode();
                return;
            }
            if (seatMode) {
                cancelSeatMode();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        orderDrawer, orderDrawerAddonOpen, popoverOpen, sidePanelModalOpen,
        salesPinOpen, onCloseSalesPin, mergeMode, seatMode,
    ]);

    // Tab label — same chain as the legacy pill row (TableServicePage.js:823).
    const optionLabel = (d) => getEntityTranslation(d, language, translationDetails) || d.name;

    const selectedObj = diningOptionObjects[selectedDiningOption] || null;

    // Faithful extraction — TableServicePage.js:297-301 (filteredOccupations).
    const filteredOccupations = occupations.filter(o => {
        if (!selectedObj) return false;
        return String(o.dining_option_id) === String(selectedObj.id);
    });

    // Faithful extraction — TableServicePage.js:306-313 (today's reservations
    // split): Upcoming = still-actionable (booked/confirmed AND not yet due);
    // everything else — past-due, seated, cancelled, no-show, completed —
    // renders dimmed under Past.
    const now = new Date();
    const upcomingReservations = todayReservations.filter(r =>
        ['booked', 'confirmed'].includes(r.status) && new Date(r.scheduled_for) >= now
    );
    const pastReservations = todayReservations.filter(r =>
        !(['booked', 'confirmed'].includes(r.status) && new Date(r.scheduled_for) >= now)
    );

    /* ── Merge mode — TableServicePage.js:498-508, 555-583 ──────────── */
    const startMergeMode = () => {
        setSeatMode(null);
        setMergeSelection([]);
        setMergeMode(true);
    };
    const cancelMergeMode = () => {
        setMergeMode(false);
        setMergeSelection([]);
    };
    const handleMergeToggle = (tableNumStr) => {
        setMergeSelection(prev => {
            const s = prev.map(String);
            return s.includes(tableNumStr) ? s.filter(x => x !== tableNumStr) : [...s, tableNumStr];
        });
    };
    const handleConfirmMerge = async () => {
        try {
            const result = await confirmMerge({
                mergeSelection, selectedDO: selectedObj, occupationService, tenantStorage,
                selectedDiningOption, pendingOrders,
            });
            if (result?.error) {
                setError(t(result.error));
                return;
            }
            cancelMergeMode();
            await refreshAll();
            setSuccess(t('tables_merged', {}, 'Tables merged'));
        } catch (e) {
            // occupationService.merge (or the order-combine step) rejected —
            // TableServicePage.js:579-582's catch. A rejection here means the
            // merge/combine sequence is in an unknown state, so — unlike the
            // guard-failure branch above, which leaves selection intact for a
            // quick retry — reset merge mode entirely and make the waiter
            // re-enter it deliberately rather than resubmit a stale selection.
            setError(e.response?.data?.detail || t('failed_to_merge'));
            cancelMergeMode();
        }
    };

    /* ── Seat / change-table mode — TableServicePage.js:365-409, 458-469 ── */
    const startChangeTable = (occ) => {
        setMergeMode(false);
        setMergeSelection([]);
        setSeatMode({
            type: 'change_table',
            occupationId: occ.id,
            fromTable: occ.table_number,
            partySize: occ.party_size,
            guestName: occ.guest_name || occ.guest?.name || '',
            label: t('change_table_mode_banner', { table: occ.table_number }, `Moving Table ${occ.table_number} — tap the table to move to`),
        });
    };
    const cancelSeatMode = () => setSeatMode(null);
    const handleSeatTarget = async (tableNumStr) => {
        if (!seatMode) return;
        const activeType = seatMode.type;
        try {
            await seatOnTable({
                seatMode, selectedDO: selectedObj, selectedDiningOption, pendingOrders,
                occupationService, waitingListService, reservationService, tenantStorage,
            }, tableNumStr);
            const msgKey = {
                walkin: 'walk_in_seated',
                waitlist: 'guest_seated_from_waitlist',
                reservation: 'reservation_seated',
                change_table: 'table_changed',
            }[activeType];
            setSeatMode(null);
            await refreshAll();
            setSuccess(t(msgKey));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_seat'));
        }
    };

    /* ── Panel: seat-from-list — TableServicePage.js:672-688 ─────────
       Both REUSE W2's seat-mode machinery wholesale: they only set
       `seatMode`; FloorCanvas shows the banner (label per the mockup's
       enterSeatMode wording, waiter-tables.html:1235) and the next tile
       tap resolves through handleSeatTarget → lib/tableFlow.seatOnTable's
       'waitlist' / 'reservation' branches. Merge mode is cancelled first —
       modes are mutually exclusive (legacy 498-503; mockup exitMerge()). */
    const seatBannerLabel = (name, party) =>
        t('seat_guest_mode_banner', { name, party }, `Seating ${name} (party of ${party}) — tap an available table`);

    const startSeatFromWaitlist = (entry) => {
        setMergeMode(false);
        setMergeSelection([]);
        const name = entry.guest?.name || entry.guest_name || t('guest');
        setSeatMode({
            type: 'waitlist',
            id: entry.id,
            partySize: entry.party_size,
            guestName: entry.guest?.name || entry.guest_name || '',
            label: seatBannerLabel(name, entry.party_size),
        });
    };

    const startSeatReservation = (res) => {
        setMergeMode(false);
        setMergeSelection([]);
        const name = res.guest?.name || res.guest_name || t('guest');
        setSeatMode({
            type: 'reservation',
            id: res.id,
            partySize: res.party_size,
            guestName: res.guest?.name || res.guest_name || '',
            label: seatBannerLabel(name, res.party_size),
        });
    };

    // Mockup [data-notify] action — no legacy handler existed, but
    // waitingListService.notify (services/api.js) has always been there;
    // POST /waiting-list/{id}/notify flips the entry to 'notified'.
    const handleNotify = async (entry) => {
        try {
            await waitingListService.notify(entry.id);
            await loadWaitlist();
            setSuccess(t('guest_notified'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_notify'));
        }
    };

    /* ── Panel: add-guest submits — TableServicePage.js:612-670 ───────
       Service half of the legacy handlers (form state lives in SidePanel).
       Returns true/false so the modal knows whether to close-and-reset —
       legacy reset its fields only inside the try branch. */
    const handleAddWaitlist = async ({ name, phone, partySize }) => {
        try {
            // Create guest first — TableServicePage.js:617-623.
            const guest = await guestService.create({ name, phone: phone || null });
            await waitingListService.add({
                guest_id: guest.id,
                party_size: partySize,
                dining_option_id: selectedObj?.id || null,
            });
            await loadWaitlist();
            setSuccess(t('added_to_waitlist'));
            return true;
        } catch (e) {
            console.error('Add to waitlist failed:', e);
            setError(e.response?.data?.detail || t('failed_to_add_waitlist'));
            return false;
        }
    };

    const handleAddReservation = async ({ name, phone, date, time, partySize, notes }) => {
        try {
            // TableServicePage.js:644-652 — scheduled_for is a real instant
            // (local wall-clock parsed then serialized), notes nullable,
            // dining option falls back to the first configured one.
            const guest = await guestService.create({ name, phone: phone || null });
            const scheduledFor = new Date(`${date}T${time}`);
            await reservationService.create({
                guest_id: guest.id,
                party_size: partySize,
                dining_option_id: selectedObj?.id || diningOptionObjects[Object.keys(diningOptionObjects)[0]]?.id,
                scheduled_for: scheduledFor.toISOString(),
                notes: notes || null,
            });
            await loadReservations();
            setSuccess(t('reservation_created'));
            return true;
        } catch (e) {
            console.error('Create reservation failed:', e);
            setError(e.response?.data?.detail || t('failed_to_create_reservation'));
            return false;
        }
    };

    /* ── Popover actions — TableServicePage.js:411-495, 585-610 ─────── */
    const handleSeatWalkIn = async (tableNumber, partySize) => {
        if (!selectedObj) return;
        try {
            await occupationService.create({
                dining_option_id: selectedObj.id,
                table_number: tableNumber,
                party_size: partySize || 2,
            });
            await refreshAll();
            setSuccess(t('walk_in_seated'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_seat'));
        }
    };

    // Faithful extraction — TableServicePage.js:429-433 (handleNewOrder).
    const handleNewOrder = (occ) => {
        if (!selectedDiningOption || !occ) return;
        setOrderDrawer({ mode: 'new', tableNumber: String(occ.table_number), occupation: occ, existingOrder: null });
    };

    // Faithful extraction — TableServicePage.js:435-443 (handleAddItems):
    // seeds existingOrder straight off the SAME pendingOrders map the floor
    // canvas already derives table status from — one source, no extra fetch.
    const handleAddItems = (occ) => {
        if (!selectedDiningOption || !occ) return;
        const tableKey = `${selectedDiningOption}-${occ.table_number}`;
        setOrderDrawer({
            mode: 'append', tableNumber: String(occ.table_number),
            occupation: occ, existingOrder: pendingOrders[tableKey] || null,
        });
    };

    // Faithful extraction — TableServicePage.js:1816-1827 (<WaiterOrderDrawer/>
    // render props) — onOrderPlaced adds a mode-appropriate success toast on
    // top of legacy's bare refreshAll, per this task's brief (legacy relied
    // on the visible floor-tile status flip alone; this surface's other
    // popover actions all toast, so the drawer follows suit for consistency).
    const handleOrderPlaced = async () => {
        await refreshAll();
        setSuccess(t(orderDrawer?.mode === 'append'
            ? 'order_updated_successfully_sent_to_kitchen'
            : 'order_submitted_successfully_sent_to_kitchen'));
    };

    const handleMarkServed = async (occ) => {
        try {
            await occupationService.markServed(occ.id);
            await refreshAll();
            setSuccess(t('marked_served'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed', {}, 'Failed'));
        }
    };
    const handleRequestBill = async (occ) => {
        try {
            await occupationService.requestBill(occ.id);
            await refreshAll();
            setSuccess(t('bill_requested'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed', {}, 'Failed'));
        }
    };
    const handleClearTable = async (occ) => {
        try {
            await occupationService.clearTable(occ.id);
            await refreshAll();
            setSuccess(t('table_cleared'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed', {}, 'Failed'));
        }
    };
    const handleSplitGroup = async (occ) => {
        try {
            await occupationService.split(occ.id);
            await refreshAll();
            setSuccess(t('group_split'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_split'));
        }
    };

    return (
        <div className="wtb-root">
            <style>{CSS}</style>

            {error && <div className="wtb-toast error" role="alert">{error}</div>}
            {success && <div className="wtb-toast success" role="status">{success}</div>}

            {/* ---- floor column ---- */}
            <div className="wtb-floorcol">
                {optionsLoaded && diningOptions.length === 0 ? (
                    <div className="wtb-nofloor" role="status">
                        <div className="wtb-nofloor-ic">{TableIcon}</div>
                        <div className="wtb-nofloor-t">{t('no_dining_options_with_tables')}</div>
                    </div>
                ) : (
                    <>
                        {/* Dining-option tabs (coord-bearing options only) + auto-refresh + merge toggle */}
                        <div className="wtb-toprow" role="tablist" aria-label={t('dining_options')}>
                            {diningOptions.map((d) => (
                                <button
                                    key={d.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={selectedDiningOption === d.name}
                                    className={`wtb-pill${selectedDiningOption === d.name ? ' active' : ''}`}
                                    onClick={() => {
                                        setSelectedDiningOption(d.name);
                                        cancelMergeMode();
                                        cancelSeatMode();
                                    }}
                                >
                                    {optionLabel(d)}
                                </button>
                            ))}
                            <span className="wtb-refresh" data-testid="auto-refresh-chip">
                                {RefreshIcon}
                                {t('auto_refresh_30s')}
                            </span>
                            {!seatMode && (
                                <button
                                    type="button"
                                    className={`wtb-merge-toggle${mergeMode ? ' on' : ''}`}
                                    aria-pressed={mergeMode}
                                    onClick={() => (mergeMode ? cancelMergeMode() : startMergeMode())}
                                >
                                    {MergeIcon}
                                    {mergeMode ? t('cancel_merge', {}, 'Cancel Merge') : t('merge_tables', {}, 'Merge Tables')}
                                </button>
                            )}
                        </div>

                        {/* Floor canvas — legend + mode banner + tiles + popover + merge bar */}
                        <FloorCanvas
                            ref={floorCanvasRef}
                            onPopoverOpenChange={setPopoverOpen}
                            key={selectedDiningOption}
                            diningOptionName={selectedDiningOption}
                            tablesConfig={selectedObj?.tables_config}
                            occupations={filteredOccupations}
                            pendingOrders={pendingOrders}
                            mergeMode={mergeMode}
                            mergeSelection={mergeSelection}
                            onMergeToggle={handleMergeToggle}
                            onMergeCancel={cancelMergeMode}
                            onMergeConfirm={handleConfirmMerge}
                            seatMode={seatMode}
                            onSeatTarget={handleSeatTarget}
                            onCancelSeatMode={cancelSeatMode}
                            onSeatWalkIn={handleSeatWalkIn}
                            onNewOrder={handleNewOrder}
                            onAddItems={handleAddItems}
                            onMarkServed={handleMarkServed}
                            onRequestBill={handleRequestBill}
                            onChangeTable={startChangeTable}
                            onSplitGroup={handleSplitGroup}
                            onClearTable={handleClearTable}
                        />
                    </>
                )}
            </div>

            {/* ---- right panel: Waitlist | Reservations (SidePanel = W3) ---- */}
            <aside className="wtb-panel" aria-label={t('guest_lists')}>
                <SidePanel
                    ref={sidePanelRef}
                    onModalOpenChange={setSidePanelModalOpen}
                    waitlistEntries={waitlistEntries}
                    upcomingReservations={upcomingReservations}
                    pastReservations={pastReservations}
                    onSeatWaitlist={startSeatFromWaitlist}
                    onSeatReservation={startSeatReservation}
                    onNotify={handleNotify}
                    onAddWaitlist={handleAddWaitlist}
                    onAddReservation={handleAddReservation}
                />
            </aside>

            {/* New Order / Add Items — always mounted, display-toggled
                (matches every other overlay's convention in this file). */}
            <OrderDrawer
                isOpen={!!orderDrawer}
                onClose={() => setOrderDrawer(null)}
                onAddonModalOpenChange={setOrderDrawerAddonOpen}
                mode={orderDrawer?.mode || 'new'}
                diningOptionName={selectedDiningOption}
                diningOptionObject={selectedObj}
                tableNumber={orderDrawer?.tableNumber}
                occupation={orderDrawer?.occupation || null}
                existingOrder={orderDrawer?.existingOrder || null}
                onOrderPlaced={handleOrderPlaced}
            />
        </div>
    );
}
